import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Step,
  StepContent,
  StepIconProps,
  StepLabel,
  Stepper,
  Typography
} from "@mui/material";
import { ChevronDown, Notebook, Pencil } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { z } from "zod";
import { colors, fontSizes } from "../theme";
import { formatDate } from "../utils";
import CardWithIcon from "./cards/CardWithIcon";
import { SelectFormInput, TextInput } from "./inputs/";
import DateInput from "./inputs/DateInput";
import SubmitButton from "./SubmitButton";
export enum ReviewStage {
  fertilization = "Fertilization",
  blastulation = "Blastulation",
  liveBirth = "Live Birth",
  liveBirth2 = "Live Birth ",
  transferOutcome = "Transfer Outcome",
  oocyteRetrieved = "Oocyte Retrieved",
}

export interface BaseReviewItem {
  id: string;
  stage: Exclude<ReviewStage, ReviewStage.liveBirth>;
}

export interface LiveBirthReviewItem extends Omit<BaseReviewItem, "stage"> {
  stage: ReviewStage.liveBirth;
  successfulPregnancy: boolean | null;
  liveBirths: number | null;
  transferDate?: Date | null;
}

export interface LiveBirthReview2Item extends Omit<BaseReviewItem, "stage"> {
  stage: ReviewStage.liveBirth2;
  liveBirths: number | null;
}

enum Outcome {
  "Failed Implantation" = "0",
  "Successful Implantation" = "1",
}

export interface TransferOutcomeItem extends Omit<BaseReviewItem, "stage"> {
  stage: ReviewStage.transferOutcome;
  transferDate: Date | null;
  outcome: Outcome | null;
  liveBirths: number | null;
}

export interface FerilizationReviewItem extends Omit<BaseReviewItem, "stage"> {
  stage: ReviewStage.fertilization;
  oocytes: number | null;
  predicted: number | null;
}
export interface BlastulationReviewItem
  extends Omit<FerilizationReviewItem, "stage"> {
  stage: ReviewStage.blastulation;
}

export type ReviewItem =
  | BaseReviewItem
  | LiveBirthReviewItem
  | TransferOutcomeItem
  | FerilizationReviewItem
  | BlastulationReviewItem
  | LiveBirthReview2Item;

interface ReviewsProps {
  reviews: ReviewItem[];
  count?: number;
  onSave: (review: ReviewItem) => void;
}

const StyledStepContent = styled(StepContent)(({ theme }) => ({
  "& .MuiCollapse-root": {
    position: "relative",
    bottom: 30,
  },
}));

const TransferOutcome: React.FC<{
  review: TransferOutcomeItem;
  editing: boolean;
  onEdit: (
    field: keyof TransferOutcomeItem,
    value: Date | string | null | number
  ) => void;
  onEditClick: () => void;
  onSaveClick: () => void;
  errors?: { [key: string]: boolean };
  helperText?: { [key: string]: string };
}> = ({
  review,
  editing,
  onEdit,
  onEditClick,
  onSaveClick,
  errors = {},
  helperText = { transferDate: " ", outcome: " " },
}) => {
  console.info("TransferOutcome", review.transferDate);

  return (
    <Card>
      <CardContent>
        <ReviewHeader
          isEditing={editing}
          onSaveClick={onSaveClick}
          title="Transfer Outcome"
          onEditClick={onEditClick}
        />
        <Stack gap={2}>
          {editing ? (
            <Stack gap={2} maxWidth={300}>
              <DateInput
                label="Transfer Date"
                error={errors["transferDate"]}
                helperText={helperText["transferDate"]}
                value={review.transferDate}
                onChange={(value: Date | null) => onEdit("transferDate", value)}
              />
              <SelectFormInput
                label="Outcome"
                error={!!errors["outcome"]}
                helperText={helperText["outcome"]}
                value={
                  review.outcome === "1"
                    ? "Successful Implantation"
                    : "Failed Implantation"
                }
                onChange={(value) =>
                  onEdit(
                    "outcome",
                    value === "Successful Implantation" ? "1" : "0"
                  )
                }
                options={[
                  {
                    label: "Successful Implantation",
                    value: "Successful Implantation",
                  },
                  {
                    label: "Failed Implantation",
                    value: "Failed Implantation",
                  },
                ]}
              />
            </Stack>
          ) : (
            <Stack direction="row" justifyContent="flex-start" gap={12}>
              <ReviewDetail
                label="Transfer Date"
                value={
                  review.transferDate ? formatDate(review.transferDate) : ""
                }
              />
              <ReviewDetail label="Outcome" value={review.outcome ?? ""} />
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const ReviewFertilization: React.FC<{
  review: FerilizationReviewItem;
  editing: boolean;
  onEdit: (field: keyof FerilizationReviewItem, value: number) => void;
  onEditClick: () => void;
  onSaveClick: () => void;
  error?: boolean;
  helperText?: string;
}> = ({
  review,
  editing,
  onEdit,
  onEditClick,
  onSaveClick,
  error,
  helperText,
}) => (
  console.info("editing", editing),
  (
    <Card sx={{ backgroundColor: colors.violet[100] }}>
      <CardContent>
        <ReviewHeader
          isEditing={editing}
          onSaveClick={onSaveClick}
          title="Fertilization"
          onEditClick={onEditClick}
        />
        <Stack gap={2}>
          {editing ? (
            <Box maxWidth={300}>
              <TextInput
                error={error}
                helperText={helperText}
                label="Number of fertilized oocytes"
                type={"number"}
                value={review.oocytes === null ? "" : Number(review.oocytes)}
                onChange={(value) => onEdit("oocytes", value as number)}
              />
            </Box>
          ) : (
            <ReviewSummary
              oocytes={review.oocytes}
              predicted={review.predicted}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  )
);

const ReviewBlastulation: React.FC<{
  review: BlastulationReviewItem;
  editing: boolean;
  onEdit: (field: keyof BlastulationReviewItem, value: number) => void;
  onEditClick: () => void;
  onSaveClick: () => void;
  error?: boolean;
  helperText?: string;
}> = ({
  review,
  editing,
  onEdit,
  onEditClick,
  onSaveClick,
  error,
  helperText,
}) => (
  <Card sx={{ backgroundColor: colors.violet[100] }}>
    <CardContent>
      <ReviewHeader
        isEditing={editing}
        onSaveClick={onSaveClick}
        title="Blastulation"
        onEditClick={onEditClick}
      />
      <Stack gap={2}>
        {editing ? (
          <Box maxWidth={300}>
            <TextInput
              type="number"
              error={error}
              helperText={helperText}
              label="Number of oocytes reach blastocyst"
              value={review.oocytes}
              onChange={(value: string | number) =>
                onEdit("oocytes", value as number)
              }
            />
          </Box>
        ) : (
          <ReviewSummary
            oocytes={review.oocytes}
            predicted={review.predicted}
          />
        )}
      </Stack>
    </CardContent>
  </Card>
);

const ReviewLiveBirth2: React.FC<{
  review: LiveBirthReview2Item;
  editing: boolean;
  onEdit: (field: keyof LiveBirthReview2Item, value: number) => void;
  onEditClick: () => void;
  onSaveClick: () => void;
  error?: boolean;
  helperText?: string;
}> = ({
  review,
  editing,
  onEdit,
  onEditClick,
  onSaveClick,
  error,
  helperText,
}) => (
  <Card sx={{ backgroundColor: colors.violet[100] }}>
    <CardContent>
      <ReviewHeader
        isEditing={editing}
        onSaveClick={onSaveClick}
        title="Live birth"
        onEditClick={onEditClick}
      />
      <Stack gap={2}>
        {editing ? (
          <Box maxWidth={300}>
            <TextInput
              type="number"
              error={error}
              helperText={helperText}
              label="Number of live births"
              value={review.liveBirths}
              onChange={(value: string | number) =>
                onEdit("liveBirths", value as number)
              }
            />
          </Box>
        ) : (
          <ReviewDetail
            label="Number of live births"
            value={review.liveBirths?.toString() || "N/A"}
          />
        )}
      </Stack>
    </CardContent>
  </Card>
);

const ReviewLiveBirth: React.FC<{
  review: LiveBirthReviewItem;
  editing: boolean;
  onEdit: (field: keyof LiveBirthReviewItem, value: any) => void;
  onEditClick: () => void;
  onSaveClick: () => void;
  error?: { [key: string]: boolean };
  helperText?: { [key: string]: string };
}> = ({
  review,
  editing,
  onEdit,
  onEditClick,
  onSaveClick,
  error = {},
  helperText = { successfulPregnancy: " ", liveBirths: " " },
}) => (
  <Card sx={{ backgroundColor: colors.violet[100] }}>
    <CardContent>
      <ReviewHeader
        isEditing={editing}
        onSaveClick={onSaveClick}
        title="Live birth"
        onEditClick={onEditClick}
      />
      <Stack gap={2}>
        {editing ? (
          <Stack direction="row" alignItems={"flex-end"} gap={2}>
            <SelectFormInput
              error={error["successfulPregnancy"]}
              helperText={helperText["successfulPregnancy"]}
              label="Was the pregnancy successful?"
              options={[
                { label: "Yes", value: "Yes" },
                { label: "No", value: "No" },
              ]}
              value={review.successfulPregnancy === true ? "Yes" : "No"}
              onChange={(value) =>
                onEdit("successfulPregnancy", value === "Yes")
              }
            />
            {review.successfulPregnancy === true && (
              <Box maxWidth={300}>
                <TextInput
                  error={error["liveBirths"]}
                  helperText={helperText["liveBirths"]}
                  label="Number of live births"
                  type="number"
                  value={review.liveBirths}
                  onChange={(value) => onEdit("liveBirths", value as number)}
                />
              </Box>
            )}
          </Stack>
        ) : (
          <Stack direction="row" justifyContent="flex-start" gap={12}>
            <ReviewDetail
              label="Successful Pregnancy?"
              value={review.successfulPregnancy ? "Yes" : "No"}
            />
            <ReviewDetail
              label="Number of live births"
              value={review.liveBirths?.toString() || "N/A"}
            />
          </Stack>
        )}
      </Stack>
    </CardContent>
  </Card>
);
const ReviewHeader: React.FC<{
  title: string;
  isEditing: boolean;
  onEditClick: () => void;
  onSaveClick: () => void;
}> = ({ title, isEditing, onEditClick, onSaveClick }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography variant="bold" fontSize={fontSizes.sm}>
      {title}
    </Typography>
    <IconButton onClick={isEditing ? onSaveClick : onEditClick}>
      {isEditing ? (
        <SubmitButton onClick={() => {}} color="greyButton">
          Save
        </SubmitButton>
      ) : (
        <Pencil />
      )}
    </IconButton>
  </Stack>
);

const ReviewSummary: React.FC<{
  oocytes: number | null;
  predicted: number | null;
}> = ({ oocytes, predicted }) => (
  <Stack direction="row" justifyContent="flex-start" gap={12}>
    <ReviewDetail
      label="Number of fertilized oocytes"
      value={oocytes?.toString() ?? ""}
    />
    <ReviewDetail label="Predicted" value={predicted?.toString() ?? ""} />
  </Stack>
);

const ReviewDetail: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Stack gap={1}>
    <Typography fontSize={fontSizes.sm} color={colors.stone[500]}>
      {label}
    </Typography>
    <Typography
      fontSize={fontSizes.sm}
      color={colors.stone[900]}
      variant="medium"
    >
      {value}
    </Typography>
  </Stack>
);

const EmptyCircleIcon: React.FC<StepIconProps> = (props) => {
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        backgroundColor: colors.violet[200],
      }}
    />
  );
};

const Reviews: React.FC<ReviewsProps> = ({
  onSave,
  count = 0,
  reviews: reviewsProp,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formErrors, setFormErrors] = React.useState<any>([]);
  const [reviewsOpen, setReviewsOpen] = useState(false);

  const handleEdit = useCallback(
    (index: number, field: keyof ReviewItem, value: any) => {
      const newReviews = [...reviews];
      newReviews[index] = { ...newReviews[index], [field]: value };
      setReviews(newReviews);
    },
    [reviews]
  );

  useEffect(() => {
    console.info("reviewsProp", { reviewsProp, count });
    if (reviewsProp) {
      setReviews(reviewsProp);

      setActiveIndex(count);
      // setEditingIndex(count);
    }
  }, [reviewsProp, count]);

  const validate = (review: ReviewItem): boolean => {
    const schema = {
      [ReviewStage.fertilization]: z.object({
        oocytes: z.number({ message: "Please enter a number" }),
      }),
      [ReviewStage.blastulation]: z.object({
        oocytes: z.number({ message: "Please enter a number" }),
      }),
      [ReviewStage.liveBirth]: z.object({
        successfulPregnancy: z.boolean({ message: "Please choose an option" }),
        liveBirths: z
          .number({ message: "Please enter a number" })
          .optional()
          .nullable(),
      }),
      [ReviewStage.liveBirth2]: z.object({
        liveBirths: z.number({ message: "Please enter a number" }),
      }),
      [ReviewStage.transferOutcome]: z.object({
        transferDate: z.date({ message: "Please enter a date" }),
        outcome: z.string({ message: "Please choose an option" }),
      }),
    };

    setFormErrors({});
    const validator = schema[review.stage as keyof typeof schema];
    const result = validator.safeParse(review);
    if (result.success) {
      return true;
    }

    setFormErrors(result.error.flatten().fieldErrors);
    return false;
  };

  const handleReviewsOpen = () => {
    setReviewsOpen(!reviewsOpen);
  };

  const renderReviewItem = useCallback(
    (review: ReviewItem, index: number) => {
      const isEditing = editingIndex === index;

      console.info("renderReviewItem", review, index, isEditing);

      const handleEditClick = () => {
        setEditingIndex(index === editingIndex ? null : index);
        setActiveIndex(index);
      };

      const handleSaveClick = async () => {
        const review = reviews[index];
        if (!validate(review)) {
          return;
        }

        try {
          await onSave(review);

          setFormErrors({});

          // if (editingIndex === reviews.length - 1) {
          //   setEditingIndex(null);
          // } else {
          //   setEditingIndex(activeIndex + 1);
          //   setActiveIndex(activeIndex + 1);
          // }

          console.info("Review saved successfully, index", index);
        } catch (error) {
          console.error("Error saving review:", error);
        }
      };

      if (review.stage === ReviewStage.fertilization) {
        return (
          <ReviewFertilization
            review={review as FerilizationReviewItem}
            editing={isEditing}
            error={formErrors["oocytes"]}
            helperText={formErrors[review.stage]}
            onEdit={(field: any, value) => handleEdit(index, field, value)}
            onEditClick={handleEditClick}
            onSaveClick={handleSaveClick}
          />
        );
      } else if (review.stage === ReviewStage.blastulation) {
        return (
          <ReviewBlastulation
            review={review as BlastulationReviewItem}
            editing={isEditing}
            error={formErrors["oocytes"]}
            helperText={formErrors[review.stage]}
            onEdit={(field: any, value) => handleEdit(index, field, value)}
            onEditClick={handleEditClick}
            onSaveClick={handleSaveClick}
          />
        );
      } else if (review.stage === ReviewStage.liveBirth) {
        return (
          <ReviewLiveBirth
            review={review as LiveBirthReviewItem}
            editing={isEditing}
            error={formErrors["liveBirths"]}
            helperText={formErrors[review.stage]}
            onEdit={(field: any, value) => handleEdit(index, field, value)}
            onEditClick={handleEditClick}
            onSaveClick={handleSaveClick}
          />
        );
      } else if (review.stage === ReviewStage.liveBirth2) {
        return (
          <ReviewLiveBirth2
            review={review as LiveBirthReview2Item}
            editing={isEditing}
            error={formErrors["liveBirths"]}
            helperText={formErrors[review.stage]}
            onEdit={(field: any, value) => handleEdit(index, field, value)}
            onEditClick={handleEditClick}
            onSaveClick={handleSaveClick}
          />
        );
      } else if (review.stage === ReviewStage.transferOutcome) {
        console.log("transferOutcome-", review);
        return (
          <TransferOutcome
            errors={formErrors}
            helperText={formErrors}
            review={review as TransferOutcomeItem}
            editing={isEditing}
            onEdit={(field: any, value) => handleEdit(index, field, value)}
            onEditClick={handleEditClick}
            onSaveClick={handleSaveClick}
          />
        );
      } else if (review.stage === ReviewStage.oocyteRetrieved) {
        return (
          <TransferOutcome
            errors={formErrors}
            helperText={formErrors}
            review={review as TransferOutcomeItem}
            editing={isEditing}
            onEdit={(field: any, value) => handleEdit(index, field, value)}
            onEditClick={handleEditClick}
            onSaveClick={handleSaveClick}
          />
        );
      }
      return null;
    },
    [editingIndex, handleEdit, onSave, reviews, formErrors]
  );

  return (
    <Accordion
      sx={{
        boxShadow: "none",
        border: "none",
        "&::before": {
          display: "none",
        },
      }}
      expanded={reviewsOpen}
      onChange={handleReviewsOpen}
    >
      <AccordionSummary
        sx={{ border: "none", padding: 0 }}
        expandIcon={<ChevronDown />}
      >
        <CardWithIcon
          sx={{ border: "none" }}
          icon={<Notebook />}
          title="Reviews"
          description="Help improve future predictions by reviewing the outcome"
        />
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap={1} width="100%">
          <Stepper
            orientation="vertical"
            activeStep={activeIndex + 1}
            sx={{ "& .MuiStepConnector-line": { display: "none" } }}
          >
            {reviews.map((review, index) => (
              <Step key={index} active={activeIndex <= index} completed={false}>
                <StepLabel
                  sx={activeIndex <= index ? { visibility: "hidden" } : {}}
                  StepIconComponent={EmptyCircleIcon}
                />
                <StyledStepContent
                  sx={activeIndex <= index ? { border: "none" } : {}}
                >
                  {renderReviewItem(review, index)}
                </StyledStepContent>
              </Step>
            ))}
          </Stepper>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default Reviews;
